import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import { CacheProvider } from '@emotion/react';
import { compose, juxt } from 'ramda';
import { AuthorizeInterceptor } from 'cx-api-client-lite';

import 'app/extensions/polyfills';

import { setDDContext, setDDUser } from 'app/selectors';
import configureStore from 'app/store/configureStore';
import { fetchUserData } from 'app/features/user/actions';
import { fetchProducts } from 'app/features/products/actions';
import { userLocale } from 'app/features/user/selectors';
import { validateAndChangeLocale } from 'app/features/localization/actions';
import { fetchDeltaConfig } from 'app/features/deltaConfig/actions';
import { gtmTrackUserReady } from 'app/analytics/actions';
import App from 'app/components/App';
import noop from 'app/libs/noop';
import { emotionCache } from 'app/styles/styledCache';
import { setClientReturnUrl } from 'app/features/checkout/actions';
import { getAnalyticsInitConfig } from 'app/analytics/selectors';
import { fetchToken } from 'app/features/authentication/actions/fetchToken';
import ErrorBoundary from 'app/components/errorPages/ErrorBoundary';
import logError from 'app/error/logError';
import redirectToFourOFour from 'app/router/helpers/redirectToFourOFour';

if (typeof document !== 'undefined') {
  const history = createBrowserHistory();
  const store = configureStore(history);
  const persistor = persistStore(store);

  store.dispatch(setClientReturnUrl());
  const initEtpAnalytics = compose(
    window.etp.init || noop,
    config => ({
      ...config,
      isEvidonEnabled: false,
    }),
    getAnalyticsInitConfig,
    store.getState,
  );

  const initDataDogContext = compose(
    juxt([setDDUser, setDDContext]),
    store.getState,
  );

  AuthorizeInterceptor.onTokenExpire(compose(store.dispatch, fetchToken));

  const fetchAppData = () =>
    store
      .dispatch(fetchUserData())
      .then(() => AuthorizeInterceptor.authorize())
      .then(() => store.dispatch(fetchProducts()))
      .then(() => store.dispatch(fetchDeltaConfig()))
      .then(initEtpAnalytics)
      .then(() => store.dispatch(gtmTrackUserReady()))
      .then(initDataDogContext)
      .then(
        compose(
          store.dispatch,
          validateAndChangeLocale,
          userLocale,
          store.getState,
        ),
      );

  const renderApp = () => {
    ReactDOM.render(
      <ErrorBoundary onError={redirectToFourOFour}>
        <CacheProvider value={emotionCache}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <App history={history} />
            </PersistGate>
          </Provider>
        </CacheProvider>
      </ErrorBoundary>,
      document.getElementById('root'),
    );
  };

  fetchAppData().then(renderApp).catch(logError);

  // Hot Module Replacement API
  if (module.hot) {
    module.hot.accept('./components/App.js', renderApp);
  }
}
